import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../api.ts';
import { Logo } from '../components/Logo.tsx';

interface Criterion {
  code: string;
  label: string;
  hint: string | null;
}

interface Info {
  extraName: string;
  companyName: string | null;
  roleTitle: string | null;
  criteria: Criterion[];
  status: 'REQUESTED' | 'CONFIRMED' | 'DECLINED' | 'EXPIRED';
  expiresAt: string;
}

type Outcome = 'ANSWERED' | 'DECLINED' | 'DELETED' | null;

export function ReferencePage() {
  const { token } = useParams();
  const [info, setInfo] = useState<Info | 'missing' | null>(null);
  const [workedWith, setWorkedWith] = useState<boolean | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [wouldRehire, setWouldRehire] = useState<boolean | null>(null);
  const [comment, setComment] = useState('');
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<Info>(`/references/${token}`).then(setInfo).catch(() => setInfo('missing'));
  }, [token]);

  async function send(action: 'ANSWER' | 'DECLINE' | 'DELETE', done: Outcome, payload: object = {}) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/references/${token}`, { action, ...payload });
      setOutcome(done);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : '';
      setError(code === 'EXPIRED' ? 'Ce lien a expiré.' : code === 'ALREADY_ANSWERED' ? 'Vous avez déjà répondu.' : 'Envoi impossible, réessayez dans un instant.');
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    send('ANSWER', 'ANSWERED', {
      workedWith,
      scores: workedWith ? scores : undefined,
      wouldRehire: workedWith ? wouldRehire : undefined,
      comment: comment || undefined,
    });
  }

  const stars = (code: string, label: string) => (
    <div style={{ display: 'flex', gap: 2 }} role="radiogroup" aria-label={label}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={scores[code] === n}
          aria-label={`${n} sur 5`}
          onClick={() => setScores((current) => ({ ...current, [code]: n }))}
          style={{ background: 'none', border: 0, padding: 2, cursor: 'pointer', fontSize: 28, lineHeight: 1, color: (scores[code] ?? 0) >= n ? '#e0a21a' : 'var(--border, #d6d3cd)' }}
        >
          ★
        </button>
      ))}
    </div>
  );

  const shell = (children: React.ReactNode) => (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '32px 20px 48px', display: 'flex', flexDirection: 'column', gap: 18 }}>
      <Logo size={26} />
      {children}
    </div>
  );

  if (info === null) return shell(<div className="empty-state">Chargement…</div>);
  if (info === 'missing') return shell(<div className="error-banner">Ce lien n'est pas valide.</div>);
  if (outcome) {
    return shell(
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontWeight: 800 }}>{outcome === 'DELETED' ? 'Vos données sont supprimées' : 'Merci !'}</div>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          {outcome === 'ANSWERED' && 'Votre réponse a bien été enregistrée. Vous pouvez fermer cette page.'}
          {outcome === 'DECLINED' && 'Nous n’utiliserons pas cette demande. Vous pouvez fermer cette page.'}
          {outcome === 'DELETED' && 'Votre adresse e-mail et vos réponses ont été effacées. Vous pouvez fermer cette page.'}
        </div>
      </div>,
    );
  }
  if (info.status === 'EXPIRED') return shell(<div className="card">Ce lien a expiré. {info.extraName} peut vous en renvoyer un nouveau.</div>);
  if (info.status !== 'REQUESTED') {
    return shell(
      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div>Vous avez déjà répondu à cette demande. Merci !</div>
        <button className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => window.confirm('Supprimer définitivement votre adresse e-mail et vos réponses ?') && send('DELETE', 'DELETED')}>
          Supprimer mes données
        </button>
      </div>,
    );
  }

  const yesNo = (value: boolean | null, set: (v: boolean) => void, name: string) => (
    <div style={{ display: 'flex', gap: 8 }} role="radiogroup" aria-label={name}>
      {[true, false].map((v) => (
        <button key={String(v)} type="button" role="radio" aria-checked={value === v} className={`btn btn-sm ${value === v ? 'btn-primary' : 'btn-secondary'}`} onClick={() => set(v)}>
          {v ? 'Oui' : 'Non'}
        </button>
      ))}
    </div>
  );

  const complete = info.criteria.every((c) => scores[c.code] >= 1);

  return shell(
    <>
      <div>
        <div style={{ fontSize: 20, fontWeight: 800 }}>Une référence pour {info.extraName}</div>
        <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginTop: 6 }}>
          {info.extraName} vous a indiqué comme référence sur Mon Extra, qui met en relation restaurateurs, traiteurs et extras indépendants
          {info.companyName ? ` (expérience chez ${info.companyName}${info.roleTitle ? `, poste : ${info.roleTitle}` : ''})` : ''}. Deux minutes, sans compte.
          Vos réponses ne sont lues que par l'équipe Mon Extra ; les restaurateurs voient seulement le nombre de références confirmées et une note globale. Données conservées 24 mois au plus.
        </div>
      </div>
      {error && <div className="error-banner">{error}</div>}
      <form onSubmit={submit} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Avez-vous travaillé avec {info.extraName} ?</div>
          {yesNo(workedWith, setWorkedWith, 'A travaillé avec cette personne')}
        </div>
        {workedWith && (
          <>
            {info.criteria.map((c) => (
              <div key={c.code}>
                <div style={{ fontWeight: 700 }}>{c.label}</div>
                {c.hint && <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginBottom: 2 }}>{c.hint}</div>}
                {stars(c.code, c.label)}
              </div>
            ))}
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>1 étoile = très insatisfaisant, 5 étoiles = excellent</div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Le ou la réembaucheriez-vous ?</div>
              {yesNo(wouldRehire, setWouldRehire, 'Réembaucherait')}
            </div>
            <div>
              <label htmlFor="comment" style={{ fontWeight: 700, display: 'block', marginBottom: 8 }}>Un commentaire (facultatif)</label>
              <textarea id="comment" className="input" rows={3} maxLength={500} value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
          </>
        )}
        <button className="btn btn-primary" disabled={busy || workedWith === null || (workedWith === true && !complete)}>Envoyer ma réponse</button>
      </form>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13 }}>
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => send('DECLINE', 'DECLINED')}>Je préfère ne pas répondre</button>
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => window.confirm('Supprimer définitivement votre adresse e-mail et vos réponses ?') && send('DELETE', 'DELETED')}>Supprimer mes données</button>
      </div>
    </>,
  );
}
